import {
  Component,
  computed,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { NgIcon, provideIcons } from "@ng-icons/core";
import { lucideTriangleAlert } from "@ng-icons/lucide";
import {
  BrnAlertDialogContent,
  HlmAlertDialog,
  HlmAlertDialogCancel,
  HlmAlertDialogContent,
  HlmButtonImports,
  HlmIconImports,
} from "@o2-fe/ui";
import {
  CustomTemplateType,
  TemplatePlaceholderMetadata,
} from "../../../../shared/interfaces/custom-template.interface";

/**
 * Non-blocking warning dialog opened when a syntactically valid template is
 * missing some of the canonical placeholders. The user is shown the list of
 * missing placeholders (with Italian descriptions) and given two choices:
 *
 *  - **Carica comunque**: emits `confirmed` so the parent proceeds with the
 *    upload, accepting that those data points won't appear in exports.
 *  - **Annulla**: emits `cancelled` and closes the dialog, leaving the user
 *    free to fix the template and try again.
 *
 * Uses the same Spartan `HlmAlertDialog` pattern as
 * `TemplateValidationErrorsDialogComponent` for visual consistency.
 */
@Component({
  selector: "app-template-validation-warnings-dialog",
  standalone: true,
  imports: [
    NgIcon,
    HlmIconImports,
    HlmButtonImports,
    HlmAlertDialog,
    HlmAlertDialogContent,
    BrnAlertDialogContent,
    HlmAlertDialogCancel,
  ],
  providers: [provideIcons({ lucideTriangleAlert })],
  templateUrl: "./template-validation-warnings-dialog.component.html",
})
export class TemplateValidationWarningsDialogComponent {
  /** ViewChild for the underlying Spartan alert dialog. */
  protected readonly dialogRef =
    viewChild.required<HlmAlertDialog>(HlmAlertDialog);

  /**
   * Resolved metadata for the missing placeholders, set by the parent via
   * `open()`. Each entry has the canonical key + Italian description so the
   * dialog can show a human-readable list.
   */
  protected readonly missingPlaceholders = signal<TemplatePlaceholderMetadata[]>(
    [],
  );

  /** Italian template-type label shown in the dialog title. */
  protected readonly templateType = signal<CustomTemplateType | null>(null);

  protected readonly templateTypeLabel = computed(() => {
    const type = this.templateType();
    if (type === "quote") return "preventivo";
    if (type === "contract") return "contratto";
    return "";
  });

  /**
   * Italian subheader phrase with correct singular/plural agreement for both
   * the noun (`segnaposto` / `segnaposti`) and the passive verb
   * (`è stato rimosso` / `sono stati rimossi`).
   */
  protected readonly missingCountPhrase = computed(() => {
    const count = this.missingPlaceholders().length;
    return count === 1
      ? "1 segnaposto è stato rimosso"
      : `${count} segnaposti sono stati rimossi`;
  });

  /** Emitted when the user clicks "Carica comunque". */
  readonly confirmed = output<void>();

  /** Emitted when the user clicks "Annulla" or closes the dialog. */
  readonly cancelled = output<void>();

  /**
   * Opens the dialog with the given list of missing placeholders.
   * Called by the parent component after `validateTemplate()` returns a
   * non-empty `missingPlaceholders` array.
   */
  open(
    missing: TemplatePlaceholderMetadata[],
    templateType: CustomTemplateType,
  ): void {
    this.missingPlaceholders.set(missing);
    this.templateType.set(templateType);
    this.dialogRef().open();
  }

  protected onConfirm(): void {
    this.dialogRef().close();
    this.confirmed.emit();
  }

  protected onCancel(): void {
    this.dialogRef().close();
    this.cancelled.emit();
  }
}
